import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SimpleFileReceiver() {
  const [businessName, setBusinessName] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setBusinessName(name);
    if (name) {
      setCustomUrl(generateSlug(name));
    } else {
      setCustomUrl('');
    }
  };

  const handleNext = async () => {
    if (!businessName || !customUrl) return;

    setIsCreating(true);

    try {
      // Create the receiving page instantly
      const response = await fetch('https://happyhumantools-d1-api.b-9f2.workers.dev/api/quick-upload-page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessName,
          slug: customUrl,
          // No auth required - instant creation
        })
      });

      const result = await response.json();

      if (result.success) {
        // Show success page with the link
        navigate(`/receiver-created/${result.data.slug}`, {
          state: {
            businessName,
            url: `https://files.happyhumantools.com/${customUrl}`,
            uploadUrl: result.data.upload_url
          }
        });
      }
    } catch (error) {
      console.error('Error creating page:', error);
      alert('Failed to create page. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo/Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            HappyHumanTools
          </h1>
          <p className="text-gray-600 text-lg">File sharing made simple</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">
            Create a File Receiving Page
          </h2>

          {/* Business Name Input */}
          <div className="mb-8">
            <label className="block text-gray-700 text-sm font-medium mb-3">
              First, what's the name of the Business or Project that needs to receive files?
            </label>
            <input
              type="text"
              value={businessName}
              onChange={handleBusinessNameChange}
              placeholder="Your business or project name"
              className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
              autoFocus
            />
            <p className="mt-2 text-sm text-gray-500">
              This helps FileInbox figure out the web address and default title of your upload page.
            </p>
          </div>

          {/* URL Preview */}
          {customUrl && (
            <div className="mb-8">
              <label className="block text-gray-700 text-sm font-medium mb-3">
                How does this web address look?
              </label>
              <div className="flex items-center">
                <span className="text-gray-500 text-lg mr-1">files.happyhumantools.com/</span>
                <input
                  type="text"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  className="flex-1 px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                />
              </div>
            </div>
          )}

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={!businessName || !customUrl || isCreating}
            className={`w-full py-4 text-lg font-medium rounded-xl transition-all ${
              businessName && customUrl && !isCreating
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isCreating ? 'Creating your page...' : 'Next'}
          </button>

          {/* Features */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">
              What you get instantly:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                '✨ Beautiful upload page',
                '🔗 Custom shareable link',
                '📁 Unlimited file uploads',
                '☁️ Secure cloud storage',
                '📱 Mobile friendly',
                '🚀 No signup required'
              ].map((feature, i) => (
                <div key={i} className="flex items-center text-gray-700">
                  <span className="text-lg">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>No credit card required • Free forever for basic use</p>
          <p className="mt-2">Files stored securely in CloudFlare R2</p>
        </div>
      </div>
    </div>
  );
}